/* global Modernizr TweenMax DOMAIN_ALT */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ShareButtons } from 'react-share';

// import Extras from '../../data/extras.json';
import Texts from '../../data/texts.json';
import Audio from '../../lib/audio';
import { isIOS, isMobile } from '../../lib/isMobile';

import Credits from './Credits.jsx';
import SoundBar from './SoundBar.jsx';

import StageActions from '../../actions/StageActions';

const {
    FacebookShareButton,
} = ShareButtons;

const propTypes = {
    opened: PropTypes.bool,
    visible: PropTypes.bool,
    offset: PropTypes.number,

    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,

    onChange: PropTypes.func,
    onClosed: PropTypes.func,
    onOpened: PropTypes.func,
    onButtonClick: PropTypes.func,
    contentMaxWidth: PropTypes.string,
    mouseDown: PropTypes.bool,

    onToggleMenu: PropTypes.func.isRequired,
    setPreviousVolume: PropTypes.func.isRequired,

    openAnimationDuration: PropTypes.number,
};

const defaultProps = {
    opened: false,
    visible: false,
    label: Texts.general.learnMore,

    visibilityAnimationDuration: 0.3,
    openAnimationDuration: 1,

    onChange: null,
    onClosed: null,
    onButtonClick: null,
    mouseDown: false,
};

const contextTypes = {
    store: PropTypes.object,
    language: PropTypes.string,
    sceneKey: PropTypes.string,
};

class TopMenu extends Component {

    static onPreventStop(e) {
        if (e) {
            e.stopPropagation();
        }
    }

    constructor(props) {
        super(props);

        this.audios = null;
        this.menuElement = null;

        this.onToggleUp = this.onToggleUp.bind(this);
        this.onToggleDown = this.onToggleDown.bind(this);
        this.onCloseClick = this.onCloseClick.bind(this);
        this.onToggleCredits = this.onToggleCredits.bind(this);

        this.updateScroll = this.updateScroll.bind(this);
        this.scrollableDiv = null;

        this.state = {
            creditsOpened: false,
        };
    }

    /*
     * Life cycle
     */

    componentDidMount() {
        this.audios = {
            appear: new Audio('/audio/open-help-menu.mp3'),
            open: new Audio('/audio/hint-button-v2-gd.mp3'),
            close: new Audio('/audio/menu-back.mp3'),
        };
        this.updateVisibility(this.props.visible, 0);
    }

    componentDidUpdate(prevProps) {
        const visibleChanged = prevProps.visible !== this.props.visible;
        const openedChanged = prevProps.opened !== this.props.opened;
        const offsetChanged = prevProps.offset !== this.props.offset;

        if (openedChanged) {
            this.updatePosition();
            if (!this.props.visible) {
                this.updateVisibility(this.props.opened);
            }
        }

        if (visibleChanged) {
            // console.log('vc');
            this.updateVisibility(this.props.visible);
        }

        if (offsetChanged) {
            // console.log('oc', this.props.offset)
            this.updateScroll();
        }
    }

    componentWillUnmount() {
        this.audios = null;
    }

    /*
     * Events handlers
     */

    onCloseClick(e) {
        if (this.props.onToggleMenu !== null) {
            this.props.onToggleMenu();
        }
    }

    onToggleDown(e) {
        e.stopPropagation();
    }

    onToggleUp(e) {
        e.stopPropagation();
    }

    onLanguageChange(e, value) {
        const { sceneKey, language } = this.context;

        if (value !== language) {
            this.context.store.dispatch(StageActions.setLanguage(value));
            const suffix = sceneKey && sceneKey !== 'intro' ? `/${sceneKey}` : '/';
            setTimeout(() => {
                if (typeof DOMAIN_ALT !== 'undefined') {
                    window.location.href = `${window.location.protocol}//${DOMAIN_ALT}${suffix}`;
                } else {
                    window.location.href = `/${value}${suffix}`;
                }
            }, 300);
        }
    }

    onToggleCredits() {
        this.setState({
            creditsOpened: !this.state.creditsOpened,
        });
    }

    /*
     * Methods
     */

    updateVisibility(visible, duration) {
        const time = typeof duration !== 'undefined' && duration !== null ?
            duration : this.props.visibilityAnimationDuration;
        const el = this.menuElement;

        if (el !== null) {
            TweenMax.to(el, time, {
                autoAlpha: visible ? 1 : 0,
            });
        }
    }

    updatePosition() {
        const opened = this.props.opened;
        const height = this.getTransitionDeltaHeight();
        const duration = this.props.openAnimationDuration;

        if (opened) {
            this.audios.open.play();
            if (this.props.onOpened) {
                this.props.onOpened(height, duration);
            }
        } else {
            this.audios.close.play();
            if (this.props.onClosed) {
                this.props.onClosed(height, duration);
            }
            this.setState({
                creditsOpened: false,
            });
        }
    }

    updateScroll() {
        const { offset } = this.props;
        if (this.scrollableDiv === null) return;

        TweenMax.to(this.scrollableDiv, 0.1, {
            y: -offset,
        });
    }

    getTransitionDeltaHeight() {
        if (this.menuElement !== null) {
            const el = this.menuElement;
            return el.offsetHeight;
        }
        return 0;
    }

    /*
     * Render
     */

    renderCloseButton() {
        const userEvents = {
            onClick: this.onCloseClick,
        };

        if (Modernizr.touchevents) {
            userEvents.onTouchStart = this.onToggleDown;
            userEvents.onTouchEnd = this.onToggleUp;
        } else {
            userEvents.onMouseDown = this.onToggleDown;
            userEvents.onMouseUp = this.onToggleUp;
        }

        const containerStyle = isMobile() ? { left: 5 } : {};
        const style = isMobile() ? { position: 'relative', top: 0, left: 0 } : { position: 'fixed' };

        return (
            <div className="close-button-container" style={containerStyle}>
                <button className="close-button" type="button" style={style} {...userEvents} />
            </div>
        );
    }

    renderOpenedContent() {
        const {
            language,
        } = this.context;

        const closeButton = this.renderCloseButton();

        const innerStyle = {
            maxWidth: this.props.contentMaxWidth,
        };

        const frClassName = classNames({
            title: true,
            inactive: language !== 'fr',
        });

        const enClassName = classNames({
            title: true,
            inactive: language !== 'en',
        });

        const creditsClassName = classNames({
            title: true,
            open: this.state.creditsOpened,
        });

        const creditsUrl = '/credits';

        return (
            <div className="opened-content">
                <div className="content" ref={(ref) => { this.scrollableDiv = ref; }}>
                    { closeButton }
                    <div className="container-content">
                        <div className="container-section">
                            <div className="center" style={innerStyle}>
                                <p className="subtitle">{language === 'en' ? 'Team' : 'Équipe'}</p>
                                { isIOS() ? (
                                    <a
                                        href={creditsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={creditsClassName}
                                    >{language === 'en' ? 'Credits' : 'Crédits'}</a>
                                ) : (
                                    <div>
                                        <button
                                            onClick={() => {
                                                this.onToggleCredits();
                                            }}
                                            className={creditsClassName}
                                        >{language === 'en' ? 'Credits' : 'Crédits'}</button>

                                    </div>
                                ) }
                            </div>
                            <Credits
                                open={this.state.creditsOpened}
                                width={this.props.width}
                                height={this.props.height}
                                maxWidth={this.props.contentMaxWidth}
                            />
                        </div>

                        <div className="container-section">
                            <div className="center" style={innerStyle}>
                                <p className="subtitle">{language === 'en' ? 'Sound' : 'Son'}</p>
                                <SoundBar
                                    mouseDown={this.props.mouseDown}
                                    width={this.props.width}
                                    height={this.props.height}
                                    setPreviousVolume={this.props.setPreviousVolume}
                                />
                            </div>
                        </div>

                        <div className="container-section">
                            <div className="center" style={innerStyle}>
                                <p className="subtitle">{language === 'en' ? 'Language' : 'Langue'}</p>
                                <button
                                    onClick={(e) => {
                                        if (language === 'fr') return;
                                        this.onLanguageChange(e, 'fr');
                                    }}
                                    className={frClassName}
                                >Français</button>
                                <button
                                    onClick={(e) => {
                                        if (language === 'en') return;
                                        this.onLanguageChange(e, 'en');
                                    }}
                                    className={enClassName}
                                >English</button>
                            </div>
                            <div className="clearfix" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const className = `menu-container ${this.props.opened ? ' opened' : ''}`;
        const openedContent = this.renderOpenedContent();

        return (
            <div className={className} ref={(el) => { this.menuElement = el; }}>
                { openedContent }
            </div>
        );
    }
}

TopMenu.propTypes = propTypes;
TopMenu.defaultProps = defaultProps;
TopMenu.contextTypes = contextTypes;

module.exports = TopMenu;



// WEBPACK FOOTER //
// ./js/components/partials/TopMenu.jsx